import { useSessionStorage } from '@oriola-corporation/kronan-hooks';
import '@oriola-corporation/ui-components/lib/esm/style.css';
import { Crown } from '@oriola-corporation/kronan-icons';
import {
  Box,
  Button,
  Forms,
  Link,
  LinkButton,
  RootThemeProvider,
  SecondaryHeading,
  Spinner,
  STATIC_COLORS,
} from '@oriola-corporation/ui-components';
import { AxiosInstance } from 'axios';
import jwtDecode from 'jwt-decode';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocalStorage } from 'react-use';
import styled from 'styled-components';
import { createAuthClient } from './auth';
import { Env } from './config';
import { login } from './login';
import { useDataDogMonitoring } from './useDataDogMonitoring';

export interface AuthContextValue {
  isLoggedIn: boolean;
  accessToken?: string;
  logout: () => void;
  authenticateAxiosClient: (axiosClient: AxiosInstance) => () => void;
}

const AuthContext = createContext<AuthContextValue>({
  isLoggedIn: false,
  logout: () => {},
  authenticateAxiosClient: () => () => {},
});

export const useAuthContext = () => useContext(AuthContext);

const isTokenValid = (accessToken?: string) => {
  if (!accessToken) return false;

  try {
    const decodedToken = jwtDecode<{ exp: number }>(accessToken);

    const currentDate = new Date();

    if (decodedToken.exp * 1000 < currentDate.getTime()) {
      return false;
    }

    return true;
  } catch (_e) {
    return false;
  }
};

interface AuthContextProviderProps {
  env: Env;
}

export const AuthContextProvider: React.FC<React.PropsWithChildren<AuthContextProviderProps>> = ({
  children,
  env,
}) => {
  useDataDogMonitoring(env);

  const [refreshToken, , clearRefreshToken] = useLocalStorage<string>('refresh_token', undefined, {
    raw: true,
  });
  const [accessToken, setAccessToken, clearAccessToken] = useSessionStorage<string>(
    'access_token',
    {
      raw: true,
    }
  );
  const [shouldRefreshToken, setShouldRefreshToken] = useState(false);

  const authClient = useMemo(() => {
    return createAuthClient(env);
  }, [env]);

  const latestTokens = useRef<{
    accessToken?: string;
    refreshToken?: string;
  }>({
    accessToken,
    refreshToken,
  });

  useEffect(() => {
    latestTokens.current = {
      accessToken,
      refreshToken,
    };
  }, [accessToken, refreshToken]);

  const logout = useCallback(() => {
    clearRefreshToken();
    clearAccessToken();
  }, [clearRefreshToken, clearAccessToken]);

  useEffect(() => {
    const updateSession = async (forceRefresh: boolean) => {
      try {
        if (!accessToken) {
          if (refreshToken) {
            console.log('Refresh token but no access token -> get access token');
            const { accessToken: newAccessToken } =
              await authClient.refreshAccessToken(refreshToken);
            setAccessToken(newAccessToken);
          } else {
            console.log('No tokens -> sign out');
            logout();
          }
        } else {
          if (!isTokenValid(accessToken)) {
            if (refreshToken) {
              console.log('Access token expired -> renew');

              const { accessToken: newAccessToken } =
                await authClient.refreshAccessToken(refreshToken);

              setAccessToken(newAccessToken);
            } else {
              console.log('Invalid access token and no refresh token -> logout');
              logout();
            }
          } else {
            if (forceRefresh && refreshToken) {
              console.log('Force refresh was requested -> refresh access token');

              const { accessToken: newAccessToken } =
                await authClient.refreshAccessToken(refreshToken);

              setAccessToken(newAccessToken);
              setShouldRefreshToken(false);
            } else {
              console.log('Valid access token -> no action');
            }
          }
        }
      } catch (_e) {
        console.log('Something went wrong when creating new token -> sign out');
        logout();
      }
    };

    updateSession(shouldRefreshToken);
  }, [accessToken, refreshToken, shouldRefreshToken, logout, setAccessToken, authClient]);

  useEffect(() => {
    if (accessToken) {
      const decodedToken = jwtDecode<{ exp: number }>(accessToken);

      const currentTime = new Date();

      const TEN_MINUTES = 10 * 60 * 1000;
      const renewTimeout = setTimeout(
        () => {
          setShouldRefreshToken(true);
        },
        decodedToken.exp * 1000 - currentTime.getTime() - TEN_MINUTES
      );

      return () => clearTimeout(renewTimeout);
    }
  }, [accessToken, clearAccessToken]);

  const isLoggedIn = isTokenValid(accessToken);

  const authenticateAxiosClient = useCallback(
    (axiosClient: AxiosInstance) => {
      const requestInterceptor = axiosClient.interceptors.request.use((config) => {
        const accessToken = latestTokens.current.accessToken;
        if (accessToken && !config.headers.Authorization) {
          config.headers.Authorization = `Bearer ${accessToken}`;
        }

        return config;
      });

      const responseInterceptor = axiosClient.interceptors.response.use(
        undefined,
        async (error) => {
          if (error.config && error.response && error.response.status === 401) {
            const refreshToken = latestTokens.current.refreshToken;
            if (refreshToken) {
              const { accessToken: newAccessToken } =
                await authClient.refreshAccessToken(refreshToken);

              setAccessToken(newAccessToken);

              error.config.headers.Authorization = `Bearer ${newAccessToken}`;

              return axiosClient.request(error.config);
            }
          }

          return Promise.reject(error);
        }
      );

      return () => {
        axiosClient.interceptors.request.eject(requestInterceptor);
        axiosClient.interceptors.response.eject(responseInterceptor);
      };
    },
    [setAccessToken, authClient]
  );

  const isLocalHost = window.location.hostname === 'localhost';

  return (
    <AuthContext.Provider
      value={{
        isLoggedIn,
        accessToken,
        authenticateAxiosClient,
        logout,
      }}
    >
      {isLoggedIn ? (
        children
      ) : !!refreshToken || !!accessToken ? (
        <div>
          <Spinner />
        </div>
      ) : (
        <RootThemeProvider>
          <Wrapper>
            <Container>
              <Box
                headerVariant={'background'}
                header={
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.625rem',
                      color: STATIC_COLORS.BRAND,
                    }}
                  >
                    <Crown size={'40px'} />
                    <SecondaryHeading withoutMargin>Ehandelsportalen inlogg</SecondaryHeading>
                  </div>
                }
              >
                {isLocalHost ? (
                  <DevLogin />
                ) : (
                  <>
                    <p>För att kunna använda ehandelsportalen behöver du logga in</p>
                    <LinkButton
                      className="min-w-0"
                      onClick={async () => {
                        await login(env);
                        window.location.reload();
                      }}
                    >
                      Logga in
                    </LinkButton>
                  </>
                )}
              </Box>
            </Container>
          </Wrapper>
        </RootThemeProvider>
      )}
    </AuthContext.Provider>
  );
};

const DevLogin = () => {
  const setTokenToLocalStorage = (value: string) => {
    localStorage.setItem('refresh_token', value);
    setTimeout(() => {
      window.location.reload();
    }, 1000);
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const token = formData.get('refreshToken');
    const tokenString = token?.toString();
    if (tokenString) setTokenToLocalStorage(tokenString);
  };

  return (
    <>
      <p>
        Use an existing refresh token to login. This is found in localStorage after successful login
        to{' '}
        <Link
          href="https://ehandelsportalen.acc1.kdapotek.io"
          target="_blank"
          rel="noopener noreferrer"
          variant="primary"
        >
          test environment
        </Link>
        .
      </p>
      <form onSubmit={handleSubmit} className="flex gap-2">
        <Forms.Elements.Input name="refreshToken" placeholder="Paste refresh_token here" />
        <Button type="submit">Login</Button>
      </form>
    </>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
  width: 100vw;
`;

const Container = styled.div`
  max-width: 500px;
`;
